import {
	InfoCard,
	LoadingPage,
	rem,
} from '@innovyze/stylovyze';
import React, { useMemo } from 'react';
import { useGlobalization } from '@Translations/useGlobalization';
import { useHistory } from 'react-router-dom';
import { Divider } from '@mui/material';
import { StyledContainer } from './FacilityPropertiesDetails.styles';
import { ImageViewer } from '../ImageViewer';
import { ItemListWrapper } from '../ItemList';

interface FacilityPropertiesDetailsProps {
	initialized: boolean;
	facilityName: string;
	sensors: string[];
	sensorGroups: string[];
	workspaceType?: 'Facility' | 'Pump Station';
	workspaceId?: string;
	imageSrc?: string;
}

export const FacilityPropertiesDetails = ({
	initialized = true,
	facilityName,
	sensors,
	sensorGroups,
	workspaceType,
	workspaceId,
	imageSrc,
}: FacilityPropertiesDetailsProps): JSX.Element => {
	const { t } = useGlobalization();
	const history = useHistory();
	// useHistory returns undefined when the library's bundled react-router-dom
	// instance isn't under a Router (e.g. yalc-linked dev builds). Fall back to
	// window.location.assign so cross-microapp navigation still works.
	const navigate = (path: string) => {
		if (history) history.push(path);
		else window.location.assign(path);
	};

	const menuItems = useMemo(() => {
		if (workspaceId) {
			if (workspaceType === 'Facility')
				return [
					{
						text: t('Open Facility', {
							context: 'Facility properties facility workspace',
						}) ?? 'Open Facility',
						cy: 'openWorkspaceButton',
						onClick: () => navigate(`/facilities/${workspaceId}`),
					},
				];
			else
				return [
					{
						text: t('Open Pump Station', {
							context:
								'Facility properties pump-station workspace',
						}) ?? 'Open Pump Station',
						cy: 'openWorkspaceButton',
						onClick: () =>
							navigate(`/pump-station/${workspaceId}`),
					},
				];
		}
		return [];
	}, [workspaceId, workspaceType]);

	return (
		<>
			<InfoCard
				title={facilityName}
				applyBodyPadding={false}
				menuItems={menuItems}
				buttonStyle={{
					variant: 'outlined',
					color: 'primary',
				}}>
				{initialized && (
					<>
						{imageSrc && (
							<ImageViewer
								imageSrc={imageSrc}
								cy="facilityImage"
							/>
						)}
						<Divider className="ContentCard--footer--divider" />
						<ItemListWrapper
							title={
								t('Groups', {
									context:
										'Facility properties sensor groups',
								}) ?? 'Groups'
							}
							items={sensorGroups}
							cy="facilitySensorGroups"
						/>
						<ItemListWrapper
							title={
								t('Sensors', {
									context: 'Facility properties sensors',
								}) ?? 'Sensors'
							}
							items={sensors}
							cy="facilitySensors"
						/>
					</>
				)}
				{!initialized && (
					<StyledContainer>
						<LoadingPage />
					</StyledContainer>
				)}
			</InfoCard>
			<Divider style={{ height: `${rem(3)}` }} />
		</>
	);
};
